import { useState, useRef, CSSProperties } from "react";
import Twemoji from "react-twemoji";
import { PuzzleType, RoundData } from "sockapi/puzzle-data";
import {
  MovableType,
  getBoxId,
  getPieceId,
} from "sockapi/scope-specs/round-spec";
import { ScopeType } from "sockapi/scope-types";
import { LargeSpinner, Spinner } from "components/Loading";
import { useRedirectIfNonAdmin } from "stores/NavStore";
import {
  useGlobalScopeState,
  useScopeStateResolvable,
} from "stores/ScopeStateStore";
import { PuzzlePageWrapper } from "pages/PuzzlePage";

import "./ArchipelagoPage.css";

function Island({
  teamId,
  displayName,
  unlockCount,
  solveCount,
  round: { roundId, gridCols, gridRows },
  onLoad,
}: {
  teamId: string;
  displayName: string;
  unlockCount: number;
  solveCount: number;
  round: RoundData;
  onLoad: () => void;
}) {
  const round = useScopeStateResolvable(ScopeType.ROUND, { teamId, roundId });
  const loaded = useRef(false);

  if (round === null) return <Spinner inline />;

  if (!loaded.current) {
    onLoad();
    loaded.current = true;
  }
  const contained = new Set(
    Object.values(round.movables).flatMap((movable) => {
      if (movable.type === MovableType.BOX_PIECE && movable.active)
        return [getPieceId(movable)];
      return [];
    })
  );

  return (
    <div className="archipelago-isle">
      <Twemoji className="archipelago-name">{displayName}</Twemoji>
      <Twemoji className="archipelago-name">
        ✅️ {solveCount} / {unlockCount}
      </Twemoji>
      <div className="archipelago-rect">
        {Object.values(round.movables).map((movable) => {
          const { type, id, cols, rows, rot } = movable;
          let { col, row } = movable;
          if (type === MovableType.PIECE && contained.has(id)) return;
          if (type === MovableType.BOX_PIECE) {
            const box = round.movables[getBoxId(movable)];
            col += box.col;
            row += box.row;
          }
          return (
            <div
              className={`archipelago-${type}`}
              key={id}
              style={{
                left: `${(100 * col) / gridCols}%`,
                top: `${(100 * row) / gridRows}%`,
                width: `${(100 * cols) / gridCols}%`,
                height: `${(100 * rows) / gridRows}%`,
                rotate: `${rot}deg`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function ArchipelagoPage() {
  useRedirectIfNonAdmin();

  const huntAdmin = useGlobalScopeState(ScopeType.HUNT_ADMIN);
  const bigBoard = useGlobalScopeState(ScopeType.BIG_BOARD);
  const [selectedRound, setSelectedRound] = useState("endgame");
  const [throttle, setThrottle] = useState(1);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  if (huntAdmin === null || bigBoard === null) return <LargeSpinner />;

  const puzzles = Object.values(huntAdmin.puzzles)
    .filter(
      (puzzle) =>
        puzzle.type === PuzzleType.PUZZLE && puzzle.roundId === selectedRound
    )
    .map((puzzle) => puzzle.puzName);
  const teams = Object.entries(bigBoard.teams)
    .flatMap(([teamId, { unlocks }]) => {
      const { displayName = "", isHidden = true } =
        huntAdmin.teamAdminData[teamId] ?? {};
      if (isHidden) return [];
      let unlockCount = 0,
        solveCount = 0;
      for (const puzName of puzzles) {
        if (!(puzName in unlocks)) continue;
        unlockCount++;
        if (unlocks[puzName].solveTime) solveCount++;
      }
      if (unlockCount === 0) return [];
      return [{ teamId, displayName, unlockCount, solveCount }];
    })
    .sort((a, b) => {
      if (a.unlockCount !== b.unlockCount) return b.unlockCount - a.unlockCount;
      if (a.solveCount !== b.solveCount) return a.solveCount - b.solveCount;
      return a.displayName.localeCompare(b.displayName);
    })
    .slice(0, throttle);

  return (
    <PuzzlePageWrapper>
      <div className="centered-page">
        <div className="archipelago-head">
          <select
            value={selectedRound}
            onChange={(evt) => {
              setSelectedRound(evt.target.value);
              setThrottle(1);
            }}
          >
            {Object.values(huntAdmin.rounds).map(({ displayName, roundId }) => (
              <option key={roundId} value={roundId}>
                {displayName}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              bodyRef.current?.classList.toggle("archipelago-heat");
            }}
          >
            Toggle heatmap mode
          </button>
        </div>
        <div
          className="archipelago-body"
          style={{ "--team-count": teams.length } as CSSProperties}
          ref={bodyRef}
        >
          {teams.map((team, i) => (
            <Island
              key={`${team.teamId}-${selectedRound}`}
              {...team}
              round={huntAdmin.rounds[selectedRound]}
              onLoad={() => setThrottle(throttle + 1)}
            />
          ))}
        </div>
      </div>
    </PuzzlePageWrapper>
  );
}
